import { IconCircleCheckedFilled } from "@/assets/icons/central/IconCircleCheckedFilled";
import { IconCircleX } from "@/assets/icons/central/IconCircleX";
import { IconArrowUp } from "@/assets/icons/geist/IconArrowUp";
import { IconChevronDown } from "@/assets/icons/geist/IconChevronDown";
import { IconFile } from "@/assets/icons/geist/IconFile";
import { IconGitHub } from "@/assets/icons/geist/IconGitHub";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { PREDEFINED_TASKS, type PredefinedTask } from "@/components/ai/ai-constants";
import ChatInputWithPrompts from "@/components/ai/chat/ChatInputWithPrompts";
import { DevRunnerSelector } from "@/components/ai/DevRunnerSelector";
import { AgentMode, applyAgentModePrefix } from "@/components/ai/prompt-input/agent-mode";
import { AgentModeDropdown } from "@/components/ai/prompt-input/AgentModeDropdown";
import ContextSelectorMenu from "@/components/ai/prompt-input/context-selector-menu/ContextSelectorMenu";
import { getContextUrlFromSelectedContext } from "@/components/ai/prompt-input/context-utils";
import { EnvironmentClassSelector } from "@/components/ai/prompt-input/EnvironmentClassSelector";
import { AddImageButton, ImageChip } from "@/components/ai/prompt-input/ImageUpload";
import {
    contextAssociatedProjectsAtom,
    DROPDOWN_MENU_KIND,
    dropdownMenuKindAtom,
    dropdownMenuWidthAtom,
    isAwaitingGitHubAuthAtom,
    isLoadingAtom,
    isResolvingStoredStateAtom,
    projectContextAtom,
    promptTextareaHeightAtom,
    selectedContextsAtom,
    selectedDevRunnerIdAtom,
    selectedEnvironmentClassAtom,
    selectedImagesAtom,
    selectedTasksAtom,
    unresolvedEnvironmentClassAtom,
    unresolvedProjectContextsAtom,
    useAutoSavePrompt,
    useClearPromptBoxState,
    usePromptAtom,
    type ImageAttachment,
    type StoredEnvironmentClass,
    type UnresolvedProjectContext,
} from "@/components/ai/prompt-input/state";
import { useImageDrop, useImagePaste } from "@/components/ai/prompt-input/use-image-drop";
import { useSubmitPrompts } from "@/components/ai/prompt-input/use-submit-prompt";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { PromptTemplate } from "@/components/flexkit/PromptTemplate";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import type { CommandSuggestion } from "@/components/podkit/forms/TextareaWithSuggestions";
import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { SourceControlProviderIcon } from "@/components/runners/details/SourceControlProviderIcon";
import { Tooltip } from "@/components/Tooltip";
import { Flags } from "@/feature-flags";
import { useAgentId } from "@/hooks/use-agent-id";
import {
    hasRunnerCapability,
    useEnvironmentClassEntries,
    type EnvironmentClassEntry,
} from "@/hooks/use-environment-class-entries";
import { useFlag, useFlagAgentImageAttachmentsEnabled, useFlagCommandsAreSkills } from "@/hooks/use-flag";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useMembership } from "@/hooks/use-membership";
import { useGetPreferredEnvironmentClassPerProject } from "@/hooks/use-preferred-env-class-per-project";
import { TrackLocations, useSegmentTrackError } from "@/hooks/use-segment";
import { useImprovePromptForAgent } from "@/queries/agent-queries";
import { getProjectInitializer } from "@/queries/environment-queries";
import { autoPaginate } from "@/queries/pagination";
import { useListProjectEnvironmentClasses, type PlainProject } from "@/queries/project-queries";
import { useListRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";
import { useEnsureOnboardingRunner, useIsUserAuthenticatedWithRunner } from "@/queries/runner-queries";
import { SCMAuthenticationModal } from "@/routes/environments/create/SCMAuthentication";
import { formatError } from "@/utils/errors";
import { isTouchDevice } from "@/utils/is-touch-device";
import { PromptInputModifierEnterKeyBinding } from "@/utils/platform";
import { Popover, PopoverAnchor, PopoverContent, PopoverPortal } from "@radix-ui/react-popover";
import { useQuery } from "@tanstack/react-query";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { RunnerCapability } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useAtom, useAtomValue, useSetAtom, useStore } from "jotai";
import {
    CircleDot,
    CornerDownLeft,
    CpuIcon,
    FocusIcon,
    FolderIcon,
    GitCommitVerticalIcon,
    GitPullRequest,
    GlobeIcon,
    TagIcon,
    WandSparklesIcon,
} from "lucide-react";
import type React from "react";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";

type NewPromptInputProps = {
    starterPrompt?: string;
    selectedCards?: PredefinedTask[];
    pendingSubmission?: PromptInputSubmission;
    onboarding?: boolean;
    onCardDismiss?: (taskName: PredefinedTask["taskName"]) => void;
    onCardUse?: (taskName: PredefinedTask["taskName"]) => void;
    onSubmit?: (submission: PromptInputSubmission) => void;
};

export type PromptInputSubmission = {
    prompts: string[];
    contexts: SelectedContext[];
    environmentClass: EnvironmentClassEntry | null;
    templateTitles?: string[];
};

export type GitCommitContext = {
    kind: "git-commit";
    url: string;
    commitHash: string;
    repoName: string;
    ownerName: string;
    scmId?: string;
};

export type PullRequestContext = {
    kind: "pr";
    url: string;
    fromBranchName: string;
    toBranchName: string;
    repoName: string;
    ownerName: string;
    title: string;
    scmId?: string;
};

export type IssueContext = {
    kind: "issue";
    url: string;
    branchName: string;
    repoName: string;
    ownerName: string;
    title: string;
    scmId?: string;
};

export type RepoContext = {
    kind: "repo";
    url: string;
    branchName: string;
    repoName: string;
    ownerName: string;
    scmId?: string;
};

export type GitTagContext = {
    kind: "git-tag";
    url: string;
    tagName: string;
    repoName: string;
    ownerName: string;
    scmId?: string;
};

export type ProjectContext = {
    kind: "project";
    project: PlainProject;
    environmentClasses: EnvironmentClassEntry[];
};

export type FromScratchContext = {
    kind: "scratch";
};

export type SelectedContext =
    | PullRequestContext
    | IssueContext
    | RepoContext
    | GitTagContext
    | ProjectContext
    | FromScratchContext
    | GitCommitContext;

// this object is purely to display a placeholder env class chip while we wait for the environment class to be loaded
const DEFAULT_ONBOARDING_ENVIRONMENT_CLASS: EnvironmentClassEntry = {
    clazz: {
        id: "01988911-9ff8-784d-ba8f-7223a7a39a11",
        displayName: "Small",
        description: "2 vCPU / 8 GiB / 50 GiB disk • m6i.large",
        configuration: [
            {
                key: "instanceType",
                value: "m6i.large",
            },
            {
                key: "diskSizeGB",
                value: "50",
            },
            {
                key: "spot",
                value: "false",
            },
            {
                key: "vcpus",
                value: "2",
            },
        ],
        runnerId: "01988911-9c64-7b2a-adee-be2657f25c26",
        enabled: true,
    },
    localRunner: false,
    policyRestricted: false,
    runner: {
        runnerId: "01988911-9c64-7b2a-adee-be2657f25c26",
        name: "Ona Cloud (EU01)",
        spec: {
            desiredPhase: 3,
            configuration: {
                region: "eu-central-1",
                releaseChannel: 1,
                autoUpdate: false,
                logLevel: 0,
                devcontainerImageCacheEnabled: true,
            },
            variant: 1,
        },
        status: {
            version: "main-gha.16116",
            systemDetails: "linux/amd64 (Linux 5.15.185, go1.24.4)",
            phase: 3,
            logUrl: "",
            message: "",
            region: "",
            additionalInfo: [
                {
                    key: "region",
                    value: "eu-central-1",
                },
            ],
            capabilities: [2, 4, 5, 3],
            llmUrl: "https://01984227-2946-7e40-a982-2f427741f5da.eu-central-1-01.gitpod.dev/runners/01988911-9c64-7b2a-adee-be2657f25c26/llm",
            publicKey: new Uint8Array(0),
            supportBundleUrl: "",
        },
        creator: {
            id: "01988911-9a57-792a-b613-9c525d1f9bf0",
            principal: 2,
        },
        kind: 2,
        provider: 4,
        runnerManagerId: "01984227-2946-7e40-a982-2f427741f5da",
    },
};

export function NewPromptInput({ selectedCards, onCardDismiss, onboarding = false, onSubmit }: NewPromptInputProps) {
    const [agentMode, setAgentMode] = useState<AgentMode>(AgentMode.Agent);
    const [showScmAuthModal, setShowScmAuthModal] = useState(false);
    const { data: runnerToUse } = useEnsureOnboardingRunner({
        enabled: onboarding,
    });
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const dropdownMenuKind = useAtomValue(dropdownMenuKindAtom);
    const setDropdownMenuKind = useSetAtom(dropdownMenuKindAtom);
    const setDropdownMenuWidth = useSetAtom(dropdownMenuWidthAtom);
    const setPromptTextareaHeight = useSetAtom(promptTextareaHeightAtom);
    const setSelectedEnvironmentClass = useSetAtom(selectedEnvironmentClassAtom);
    const setSelectedImages = useSetAtom(selectedImagesAtom);
    const promptTextareaRef = useRef<HTMLTextAreaElement>(null);
    const api = useGitpodAPI();
    const trackError = useSegmentTrackError();
    const { toast } = useToast();

    // Image drag-and-drop support
    const { value: imageAttachmentsEnabled } = useFlagAgentImageAttachmentsEnabled();
    const handleImagesDropped = useCallback(
        (images: ImageAttachment[]) => {
            setSelectedImages((prev) => [...prev, ...images]);
        },
        [setSelectedImages],
    );
    const { isDragging, dragHandlers } = useImageDrop(handleImagesDropped, !imageAttachmentsEnabled || onboarding);
    const { handlePaste: handleImagePaste } = useImagePaste(
        handleImagesDropped,
        !imageAttachmentsEnabled || onboarding,
    );

    // Get selected contexts and environment class for authentication check
    const selectedContexts = useAtomValue(selectedContextsAtom);
    const selectedEnvironmentClass = useAtomValue(selectedEnvironmentClassAtom);

    // Note(mads): This is best effort right now - we don't support selecting different env classes for projects right now
    // and don't support the default env classes that are stored in user preferences.
    // Tracked in AGENT-875 to support multi-environment-class projects in this flow.
    const projectId = selectedContexts.find((ctx) => ctx.kind === "project")?.project.id;
    const { data: projectEnvironmentClasses, isPending: isProjectEnvClassesPending } =
        useListProjectEnvironmentClasses(projectId);
    const { environmentClassEntries, isLoading: isPendingEnvClassEntries } = useEnvironmentClassEntries();
    const defaultEnvClassIdForProject =
        projectEnvironmentClasses?.environmentClasses[0]?.environmentClass.case === "environmentClassId" &&
        projectEnvironmentClasses?.environmentClasses[0]?.environmentClass.value;

    const environmentClass = useMemo(() => {
        return projectId && defaultEnvClassIdForProject
            ? environmentClassEntries.find((entry) => entry.clazz?.id === defaultEnvClassIdForProject)
            : selectedEnvironmentClass;
    }, [defaultEnvClassIdForProject, environmentClassEntries, projectId, selectedEnvironmentClass]);
    const contextUrl = selectedContexts.map((c) => getContextUrlFromSelectedContext(c))[0];
    const { data: authCheckResponse, isPending: isAuthCheckPending } = useIsUserAuthenticatedWithRunner(
        environmentClass?.runner?.runnerId,
        contextUrl,
        {
            refetchUntilAuthenticated: true,
            enabled: Boolean(contextUrl),
        },
    );
    const isResolvingStoredState = useAtomValue(isResolvingStoredStateAtom);
    const isSubmitDisabled =
        authCheckResponse?.type === "AuthenticationRequired" ||
        (contextUrl && isAuthCheckPending) ||
        (Boolean(projectId) && (isProjectEnvClassesPending || isPendingEnvClassEntries)) ||
        isResolvingStoredState;

    const resizeObserver = useMemo(
        () =>
            new ResizeObserver((entries) => {
                for (const entry of entries) {
                    if (entry.target === inputContainerRef.current) {
                        setDropdownMenuWidth(entry.target.clientWidth);
                    } else if (entry.target === promptTextareaRef.current) {
                        setPromptTextareaHeight(entry.target.clientHeight);
                    }
                }
            }),
        [setDropdownMenuWidth, setPromptTextareaHeight],
    );

    useAutoSavePrompt();

    useEffect(() => {
        if (inputContainerRef.current) {
            resizeObserver.observe(inputContainerRef.current);
        }
        if (promptTextareaRef.current) {
            resizeObserver.observe(promptTextareaRef.current);
        }
        return () => {
            resizeObserver.disconnect();
        };
    });

    useEffect(() => {
        if (dropdownMenuKind === DROPDOWN_MENU_KIND.none && promptTextareaRef.current) {
            promptTextareaRef.current.focus();
        }
    }, [dropdownMenuKind]);

    const { data: environmentClassForOnboarding, error: environmentClassForOnboardingQueryError } = useQuery({
        queryKey: ["environment-classes", "onboarding", runnerToUse?.runnerId],
        queryFn: async (): Promise<EnvironmentClassEntry | null> => {
            if (!runnerToUse) return null;

            const environmentClasses = await autoPaginate(
                (params) => api.environmentService.listEnvironmentClasses(params),
                {
                    filter: {
                        runnerIds: [runnerToUse.runnerId],
                    },
                },
                (response) => response.environmentClasses,
            );

            let entry: EnvironmentClassEntry | null = null;
            // the api currently does not have a reliable mechanism for ordering the environment classes by size
            // we have to rely on the display name to find the smallest environment class
            for (const clazz of environmentClasses) {
                if (clazz.displayName.toLowerCase().includes("small")) {
                    entry = {
                        clazz,
                        runner: runnerToUse,
                        localRunner: false,
                        policyRestricted: false,
                    };
                }
            }

            // if we didn't find a small environment class, we'll use the first one
            if (!entry && environmentClasses.length > 0) {
                const firstClass = environmentClasses[0];
                entry = {
                    clazz: firstClass,
                    runner: runnerToUse,
                    localRunner: false,
                    policyRestricted: false,
                };
            }

            return entry;
        },
        enabled: onboarding && !!runnerToUse,
    });

    useEffect(() => {
        if (environmentClassForOnboarding) {
            setSelectedEnvironmentClass(environmentClassForOnboarding);
        } else if (onboarding) {
            // if we don't have an environment class for onboarding, we'll use the "default" one
            // to fake the appearance while we wait for the environment class to be loaded
            setSelectedEnvironmentClass(DEFAULT_ONBOARDING_ENVIRONMENT_CLASS);
        }
    }, [environmentClassForOnboarding, onboarding, setSelectedEnvironmentClass]);

    useEffect(() => {
        if (environmentClassForOnboardingQueryError) {
            toast({
                title: "Failed to find an environment class",
                description: formatError(environmentClassForOnboardingQueryError),
            });
            trackError("onboarding_find_environment_class_error", environmentClassForOnboardingQueryError);
            console.error(
                "failed to find an environment class for onboarding",
                environmentClassForOnboardingQueryError,
            );
        }
    }, [environmentClassForOnboardingQueryError, trackError, toast]);

    let content: React.ReactNode;
    if (onboarding) {
        content = <PredefinedTaskCards />;
    } else if (selectedCards && selectedCards.length > 0) {
        content = <SelectedCards selectedCards={selectedCards} onCardDismiss={onCardDismiss} />;
    } else {
        content = <PromptTextarea ref={promptTextareaRef} agentMode={agentMode} onPaste={handleImagePaste} />;
    }

    return (
        <Popover
            open={dropdownMenuKind !== DROPDOWN_MENU_KIND.none}
            onOpenChange={(open) => {
                if (!open) {
                    setDropdownMenuKind(DROPDOWN_MENU_KIND.none);
                }
            }}
        >
            <div
                className={cn(
                    "flex flex-col gap-0.5 rounded-xl border border-border-subtle bg-surface-muted p-1.5",
                    isDragging && "ring-2 ring-content-accent ring-offset-2",
                )}
                {...dragHandlers}
            >
                <div className="flex flex-col overflow-clip rounded-lg border border-border-base">
                    <PopoverAnchor>
                        <div ref={inputContainerRef} className="bg-white p-2 dark:bg-surface-primary">
                            {content}
                            <ButtonRow
                                onboarding={onboarding}
                                selectedCards={selectedCards}
                                onCardDismiss={onCardDismiss}
                                onSubmit={onSubmit}
                                isSubmitDisabled={isSubmitDisabled}
                                agentMode={agentMode}
                                onAgentModeChange={setAgentMode}
                            />
                        </div>
                    </PopoverAnchor>
                </div>
                <ChipArea onboarding={onboarding} />
            </div>
            {authCheckResponse?.type === "AuthenticationRequired" && (
                <div className="mt-2">
                    <Banner
                        variant="warning"
                        LeadingIcon={null}
                        action={{
                            text: "Authenticate",
                            onClick: () => {
                                setShowScmAuthModal(true);
                            },
                            "data-tracking-id": "authenticate-scm-prompt-input",
                        }}
                        text={
                            <span>
                                <span className="font-bold">{environmentClass?.runner?.name || "Unknown runner"}</span>{" "}
                                requires authentication with{" "}
                                <span className="font-bold">{authCheckResponse.scmName}</span>
                            </span>
                        }
                        className="shadow-none"
                    />
                </div>
            )}
            {environmentClass && !hasRunnerCapability(environmentClass, RunnerCapability.AGENT_EXECUTION) && (
                <div className="mt-2">
                    <Banner
                        variant="warning"
                        LeadingIcon={null}
                        action={{
                            text: "Change",
                            onClick: () => {
                                setSelectedEnvironmentClass(null);
                                setDropdownMenuKind(DROPDOWN_MENU_KIND.environmentClassSelector);
                            },
                            "data-tracking-id": "agent-not-supported-banner-change-environment-class",
                        }}
                        text={
                            <span>
                                <span className="font-bold">{environmentClass?.runner?.name || "Unknown runner"}</span>{" "}
                                does not support Ona agent.
                            </span>
                        }
                        className="shadow-none"
                    />
                </div>
            )}
            <PopoverPortal>
                <PopoverContent side="bottom" avoidCollisions={false}>
                    <ActiveMenu />
                </PopoverContent>
            </PopoverPortal>
            {showScmAuthModal && authCheckResponse?.type === "AuthenticationRequired" && (
                <SCMAuthenticationModal
                    repoURL={contextUrl}
                    authenticationUrl={authCheckResponse.url}
                    patAuth={authCheckResponse.patAuth}
                    scmId={authCheckResponse.scmId}
                    scmName={authCheckResponse.scmName}
                    runnerId={environmentClass?.runner?.runnerId || ""}
                    onClose={() => setShowScmAuthModal(false)}
                    onClickContinue={() => {
                        setShowScmAuthModal(false);
                        // The useCheckSCMAuthentication hook will automatically refetch and update the authentication status
                    }}
                    data-track-location={TrackLocations.AIPromptSCMAuthenticationModal}
                />
            )}
        </Popover>
    );
}
NewPromptInput.displayName = "NewPromptInput";

const PromptTextarea = forwardRef<
    HTMLTextAreaElement,
    { agentMode: AgentMode; onPaste?: React.ClipboardEventHandler<HTMLTextAreaElement> }
>(({ agentMode, onPaste }, ref) => {
    const [prompt, setPrompt] = useAtom(usePromptAtom());
    const { submitPrompts, isSubmittingPrompts } = useSubmitPrompts();
    const setDropdownMenuKind = useSetAtom(dropdownMenuKindAtom);
    const clearPromptBoxState = useClearPromptBoxState();
    const store = useStore();
    const setIsLoading = useSetAtom(isLoadingAtom);
    const { membership } = useMembership();
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;
    const { value: commandsAreSkills } = useFlagCommandsAreSkills();
    const { value: imageAttachmentsEnabled } = useFlagAgentImageAttachmentsEnabled();
    const selectedImages = useAtomValue(selectedImagesAtom);
    const selectedDevRunnerId = useAtomValue(selectedDevRunnerIdAtom);
    const hasMovedCursorToEnd = useRef(false);

    useEffect(() => {
        setIsLoading(isSubmittingPrompts);
    }, [isSubmittingPrompts, setIsLoading]);

    // Move cursor to end of prompt on initial load (when restored from localStorage)
    useEffect(() => {
        if (hasMovedCursorToEnd.current || !prompt) {
            return;
        }
        const textarea = typeof ref === "function" ? null : ref?.current;
        if (textarea) {
            textarea.setSelectionRange(prompt.length, prompt.length);
            hasMovedCursorToEnd.current = true;
        }
    }, [prompt, ref]);

    function _setPrompt(event: React.ChangeEvent<HTMLTextAreaElement>) {
        setPrompt(event.currentTarget.value);
    }

    const handleSuggestionSelect = useCallback(
        (suggestion: CommandSuggestion) => {
            // Check if this is a builtin command
            if (suggestion.id.startsWith("builtin-")) {
                // Execute builtin commands directly - handle /skills or /commands based on feature flag
                const manageCommand = commandsAreSkills ? "/skills" : "/commands";
                if (suggestion.value === manageCommand && isOrgAdmin) {
                    window.open("/settings/agents", "_blank");
                    return true; // Prevent default pasting behavior
                }
            }
            // For non-builtin commands, allow the default pasting behavior
            return false;
        },
        [isOrgAdmin, commandsAreSkills],
    );

    function onKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
        // Only handle Enter key events
        if (event.key !== "Enter") {
            return;
        }

        const hasMeta = event.metaKey && !event.ctrlKey;
        const hasCtrl = event.ctrlKey && !event.metaKey;
        const hasShift = event.shiftKey;

        // Allow Shift+Enter for new lines (default textarea behavior)
        if (hasShift) {
            return;
        }

        // On touch devices, let Enter insert newlines (users submit via Send button)
        if (isTouchDevice()) {
            return;
        }

        // Handle Enter and Cmd+Enter/Ctrl+Enter
        const isModifierEnter = hasMeta || hasCtrl;
        const isPlainEnter = !isModifierEnter;

        if (!isPlainEnter && !isModifierEnter) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (isSubmittingPrompts) {
            return;
        }

        // Handle built-in commands - /skills or /commands based on feature flag
        const trimmedPrompt = prompt.trim();
        const manageCommand = commandsAreSkills ? "/skills" : "/commands";
        if (trimmedPrompt === manageCommand && isOrgAdmin) {
            setPrompt("");
            window.open("/settings/agents", "_blank");
            return;
        }

        const contexts = store.get(selectedContextsAtom);
        if (contexts.length === 0) {
            setDropdownMenuKind(DROPDOWN_MENU_KIND.context);
            return;
        }

        if (!prompt) {
            return;
        }

        const environmentClass = store.get(selectedEnvironmentClassAtom);
        const hasProjectContext = contexts.some((ctx) => ctx.kind === "project");

        // If no project context and no environment class selected, open the environment class selector
        if (!hasProjectContext && !environmentClass) {
            setDropdownMenuKind(DROPDOWN_MENU_KIND.environmentClassSelector);
            return;
        }

        // Plain Enter navigates to conversation, Cmd+Enter stays on current page
        const shouldNavigate = isPlainEnter;

        const imagesToSend = imageAttachmentsEnabled ? selectedImages : undefined;

        void submitPrompts({
            prompts: [applyAgentModePrefix(prompt, agentMode)],
            contexts,
            environmentClass,
            shouldNavigate,
            images: imagesToSend,
            devRunnerId: selectedDevRunnerId ?? undefined,
        }).then((isCompleteFailure) => {
            if (!isCompleteFailure) {
                clearPromptBoxState();
            }
        });
    }

    return (
        <ChatInputWithPrompts
            ref={ref}
            className="max-w-full p-2"
            placeholder="Ask Ona to..."
            data-testid="prompt-input-textarea"
            rows={5}
            minHeight={116}
            maxHeight={400}
            value={prompt}
            onChange={_setPrompt}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onSuggestionSelect={handleSuggestionSelect}
            popoverSideOffset={44} // put the command popover below the action row of the prompt input
            popoverWidthOffset={16} // account for the total padding
            popoverAlignOffset={-8} // account for the left padding
            popoverSide="bottom" // don't cover the Ona logo
            showBuiltInCommands={[commandsAreSkills ? "/skills" : "/commands"]} // Only show manage command on home page
            disabledMessage={
                agentMode === AgentMode.Ralph
                    ? commandsAreSkills
                        ? "Skills are not available in Ralph mode. Switch to Agent mode to use skills."
                        : "Commands are not available in Ralph mode. Switch to Agent mode to use commands."
                    : undefined
            }
        />
    );
});
PromptTextarea.displayName = "PromptTextarea";

function PredefinedTaskCards() {
    const [selectedTasks, setSelectedTasks] = useAtom(selectedTasksAtom);

    function handleTaskSelectChange(task: PredefinedTask, checked: boolean) {
        if (checked) {
            setSelectedTasks([...selectedTasks, task]);
        } else if (selectedTasks.length > 1) {
            setSelectedTasks(selectedTasks.filter((t) => t !== task));
        }
    }

    return (
        <ul className="flex flex-col gap-2 pb-2 md:flex-row" role="listbox" aria-orientation="horizontal">
            {PREDEFINED_TASKS.map((task) => {
                const isSelected = selectedTasks.includes(task);
                return (
                    <li key={task.taskName} className="contents">
                        <PromptTemplate
                            className="w-full"
                            Icon={task.Icon}
                            iconColor={task.iconColor}
                            iconBackgroundColor={task.iconBackgroundColor}
                            title={task.title}
                            description={task.description}
                            selected={isSelected}
                            selectionBehavior="select"
                            data-tracking-id={`select-prompt-task-card-${task.taskName}-prompt-input`}
                            onSelectChange={(checked) => handleTaskSelectChange(task, checked)}
                        />
                    </li>
                );
            })}
        </ul>
    );
}

function ButtonRow({
    selectedCards,
    onboarding,
    onCardDismiss,
    onSubmit,
    isSubmitDisabled,
    agentMode,
    onAgentModeChange,
}: {
    selectedCards?: PredefinedTask[];
    onboarding: boolean;
    onCardDismiss?: (taskName: PredefinedTask["taskName"]) => void;
    onSubmit?: (submission: PromptInputSubmission) => void;
    isSubmitDisabled: boolean;
    agentMode: AgentMode;
    onAgentModeChange: (mode: AgentMode) => void;
}) {
    const { value: isSpecModeEnabled } = useFlag(Flags.SpecModeEnabled, false);
    const { value: isRalphModeEnabled } = useFlag(Flags.RalphModeEnabled, false);

    // Build available agent modes based on feature flags
    const availableAgentModes = useMemo(() => {
        const modes: AgentMode[] = [AgentMode.Agent];
        if (isSpecModeEnabled) {
            modes.push(AgentMode.Spec);
        }
        if (isRalphModeEnabled) {
            modes.push(AgentMode.Ralph);
        }
        return modes;
    }, [isSpecModeEnabled, isRalphModeEnabled]);

    const showAgentModeDropdown = availableAgentModes.length > 1;

    const { toast } = useToast();
    const [isAwaitingGitHubAuth, setIsAwaitingGitHubAuth] = useAtom(isAwaitingGitHubAuthAtom);
    const { data: runnerToUse, error: ensureOnboardingRunnerError } = useEnsureOnboardingRunner({
        enabled: onboarding,
    });
    const { data: runnerSCMIntegrations } = useListRunnerSCMIntegrations(runnerToUse?.runnerId, {
        enabled: !!runnerToUse && onboarding,
    });
    const githubIntegration = useMemo(() => {
        if (!onboarding || !runnerSCMIntegrations) return null;
        return runnerSCMIntegrations.find((integration) => integration.host === "github.com");
    }, [onboarding, runnerSCMIntegrations]);
    const {
        data: githubAuthStatus,
        isLoading: isLoadingGithubAuth,
        error: checkGitHubAuthError,
    } = useIsUserAuthenticatedWithRunner(runnerToUse?.runnerId, githubIntegration ? "https://github.com" : undefined, {
        enabled: onboarding && !!githubIntegration && !!runnerToUse?.runnerId,
        refetchUntilAuthenticated: isAwaitingGitHubAuth,
        retryOnFailedPrecondition: true,
    });
    const trackError = useSegmentTrackError();

    useEffect(() => {
        if (isAwaitingGitHubAuth && githubAuthStatus?.type === "Authenticated") {
            setIsAwaitingGitHubAuth(false);
        }
    }, [githubAuthStatus?.type, isAwaitingGitHubAuth, setIsAwaitingGitHubAuth]);

    useEffect(() => {
        if (ensureOnboardingRunnerError) {
            toast({
                title: "Failed to find a runner",
                description: formatError(ensureOnboardingRunnerError),
            });
            trackError("onboarding_ensure_runner_error", ensureOnboardingRunnerError);
            console.error("failed to find a runner", ensureOnboardingRunnerError);
        }
    }, [toast, ensureOnboardingRunnerError, trackError]);

    useEffect(() => {
        if (checkGitHubAuthError) {
            toast({
                title: "Failed to check GitHub authentication",
                description: formatError(checkGitHubAuthError),
            });
            trackError("onboarding_check_github_auth_error", checkGitHubAuthError);
            console.error("failed to check github auth", checkGitHubAuthError);
        }
    }, [checkGitHubAuthError, trackError, toast]);

    function handleConnectToGitHub() {
        if (githubAuthStatus?.type === "AuthenticationRequired" && githubAuthStatus.url) {
            window.open(githubAuthStatus.url, "_blank");
            setIsAwaitingGitHubAuth(true);
            toast({
                title: "Authentication required",
                description: "Please authenticate with GitHub in the new window",
            });
        }
    }

    function clearCards() {
        if (selectedCards && onCardDismiss) {
            for (const card of selectedCards) {
                onCardDismiss(card.taskName);
            }
        }
    }

    const needsGitHubAuth = onboarding && githubIntegration && githubAuthStatus?.type === "AuthenticationRequired";
    const isAuthStatusLoading = isLoadingGithubAuth && !githubAuthStatus;
    const shouldShowGitHubButton = onboarding && (needsGitHubAuth || isAuthStatusLoading);

    const selectedContexts = useAtomValue(selectedContextsAtom);
    const selectedEnvironmentClass = useAtomValue(selectedEnvironmentClassAtom);
    const completed = isContextComplete(selectedContexts, selectedEnvironmentClass);

    let contextButtons = null;
    if (completed) {
        contextButtons = <ChipContextAdded />;
    } else if (onboarding) {
        if (!needsGitHubAuth && !isAuthStatusLoading) {
            contextButtons = (
                <>
                    <AddRepositoryButton />
                    <SelectEnvironmentClassButton />
                </>
            );
        }
    } else {
        contextButtons = (
            <>
                <AddContextButton />
                <SelectProjectButton />
                <SelectEnvironmentClassButton />
            </>
        );
    }

    const { value: imageAttachmentsEnabled } = useFlagAgentImageAttachmentsEnabled();
    const setSelectedImages = useSetAtom(selectedImagesAtom);
    const handleImagesSelected = useCallback(
        (images: ImageAttachment[]) => {
            setSelectedImages((prev) => [...prev, ...images]);
        },
        [setSelectedImages],
    );

    return (
        <div className="flex h-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-none sm:flex-row sm:items-center">
                {shouldShowGitHubButton ? (
                    <ConnectToGitHubButton
                        isLoading={isLoadingGithubAuth || isAwaitingGitHubAuth}
                        onClick={handleConnectToGitHub}
                        data-tracking-id-none
                        data-tracking-id="connecttogithub" // onClick is passed down to children
                    />
                ) : null}
                {contextButtons}
            </div>
            <div className="flex min-w-0 flex-row flex-wrap items-center gap-2">
                {imageAttachmentsEnabled && !onboarding && <AddImageButton onImagesSelected={handleImagesSelected} />}
                {/* <ImprovePromptButton selectedCards={selectedCards} /> */}
                <DevRunnerSelector />
                {showAgentModeDropdown && (
                    <AgentModeDropdown
                        mode={agentMode}
                        onModeChange={onAgentModeChange}
                        availableModes={availableAgentModes}
                    />
                )}
                <SubmitPromptButton
                    selectedCards={selectedCards}
                    onboarding={onboarding}
                    onSubmitSuccessful={clearCards}
                    onSubmit={onSubmit}
                    isDisabled={isSubmitDisabled}
                    agentMode={agentMode}
                />
            </div>
        </div>
    );
}

// ImprovePromptButton is unused because it doesn't work
// but when it works, it'll be put back into the UI.
// @ts-expect-error - Function is intentionally unused but kept for future use
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ImprovePromptButton({ selectedCards }: { selectedCards?: PredefinedTask[] }) {
    const promptAtom = usePromptAtom();
    const [prompt, setPrompt] = useAtom(promptAtom);
    const agentId = useAgentId();
    const improvePromptForAgent = useImprovePromptForAgent();
    const { toast } = useToast();
    const store = useStore();

    async function handleImprovePrompt() {
        try {
            const currentPrompt = store.get(promptAtom);
            const { improvedPrompt } = await improvePromptForAgent.mutateAsync({ agentId, prompt: currentPrompt });
            setPrompt(improvedPrompt);
        } catch (error) {
            toast({
                title: "Failed to improve prompt",
                description: formatError(error),
            });
        }
    }

    const hasSelectedCards = (selectedCards?.length ?? 0) > 0;
    const cannotImprove = !prompt || hasSelectedCards || !agentId;

    return (
        <Tooltip content="Improve prompt" usePortal>
            <Button
                type="button"
                variant="secondary"
                disabled={cannotImprove}
                loading={improvePromptForAgent.isPending}
                LeadingIcon={(props) => <WandSparklesIcon {...props} size={16} />}
                onClick={handleImprovePrompt}
                className="m-0 h-8 flex-1 p-2 sm:aspect-square sm:flex-none"
                data-testid="prompt-input-improve-prompt-button"
                data-tracking-id="improve-prompt-prompt-input"
            >
                <span className="sm:sr-only">Improve prompt</span>
            </Button>
        </Tooltip>
    );
}

function SubmitPromptButton({
    selectedCards,
    onboarding,
    onSubmitSuccessful,
    onSubmit,
    isDisabled,
    agentMode,
}: {
    selectedCards?: PredefinedTask[];
    onboarding: boolean;
    onSubmitSuccessful: () => void;
    onSubmit?: (submission: PromptInputSubmission) => void;
    isDisabled: boolean;
    agentMode: AgentMode;
}) {
    const agentId = useAgentId();
    const [prompt] = useAtom(usePromptAtom());
    const [isLoading, setIsLoading] = useAtom(isLoadingAtom);
    const selectedTasks = useAtomValue(selectedTasksAtom);
    const selectedContexts = useAtomValue(selectedContextsAtom);
    const selectedEnvironmentClass = useAtomValue(selectedEnvironmentClassAtom);
    const selectedDevRunnerId = useAtomValue(selectedDevRunnerIdAtom);
    const hasProjectContext = useAtomValue(projectContextAtom);
    const selectedImages = useAtomValue(selectedImagesAtom);
    const { value: imageAttachmentsEnabled } = useFlagAgentImageAttachmentsEnabled();
    const { submitPrompts, isSubmittingPrompts } = useSubmitPrompts();
    const clearPromptBoxState = useClearPromptBoxState();

    useEffect(() => {
        setIsLoading(isSubmittingPrompts);
    }, [isSubmittingPrompts, setIsLoading]);

    async function submit() {
        let prompts: string[] = [];
        let templateTitles: string[] | undefined;

        if (onboarding && selectedTasks.length > 0) {
            templateTitles = [];
            for (const task of selectedTasks) {
                prompts.push(task.prompt);
                templateTitles.push(task.title);
            }
        } else if (selectedCards && selectedCards.length > 0) {
            templateTitles = [];
            for (const card of selectedCards) {
                prompts.push(card.prompt);
                templateTitles.push(card.title);
            }
        } else if (prompt) {
            prompts = [applyAgentModePrefix(prompt, agentMode)];
        }

        if (onSubmit) {
            onSubmit({
                prompts,
                contexts: selectedContexts,
                environmentClass: selectedEnvironmentClass,
                templateTitles,
            });
            clearPromptBoxState();
            return;
        }

        if (prompts.length > 0) {
            const imagesToSend = imageAttachmentsEnabled ? selectedImages : undefined;
            const isCompleteFailure = await submitPrompts({
                prompts,
                contexts: selectedContexts,
                environmentClass: selectedEnvironmentClass,
                templateTitles,
                images: imagesToSend,
                devRunnerId: selectedDevRunnerId ?? undefined,
            });
            if (!isCompleteFailure) {
                clearPromptBoxState();
                onSubmitSuccessful();
            }
        }
    }

    let disabled: boolean;
    if (onboarding) {
        disabled = selectedTasks.length === 0 || selectedContexts.length === 0;
    } else {
        disabled =
            isDisabled ||
            (!prompt && selectedCards?.length === 0) ||
            !agentId ||
            selectedContexts.length === 0 ||
            (!hasProjectContext && !selectedEnvironmentClass) ||
            isLoading;
    }

    const buttonElement = (
        <Button
            type="submit"
            variant="primary"
            disabled={disabled}
            loading={isLoading}
            LeadingIcon={(props) =>
                onboarding ? <IconArrowUp {...props} size="sm" /> : <CornerDownLeft {...props} size={16} />
            }
            size="md"
            className={cn("m-0 h-8 flex-1 p-2", { "sm:aspect-square sm:flex-none": !onboarding })}
            data-testid="prompt-input-submit-button"
            onClick={submit}
            data-tracking-id="submit-prompt-prompt-input"
        >
            <span
                className={cn({
                    "sm:sr-only": !onboarding || (onboarding && disabled),
                })}
            >
                {(() => {
                    if (onboarding) {
                        return "Start tasks";
                    }
                    if (selectedCards && selectedCards.length > 0) {
                        return "Start agents";
                    }
                    return "Submit";
                })()}
            </span>
        </Button>
    );

    if (onboarding) {
        return buttonElement;
    }

    return (
        <Tooltip
            content={
                <div className="flex flex-col gap-2">
                    {(!selectedCards || selectedCards.length === 0) && (
                        <>
                            <div className="flex flex-row items-center justify-between gap-2">
                                <span>Submit and go to conversation</span>
                                <div className="flex items-center gap-1">
                                    <KeyboardCombo keys={["Enter"]} />
                                </div>
                            </div>
                            <div className="flex flex-row items-center justify-between gap-2">
                                <span>Submit and stay on page</span>
                                <div className="flex items-center gap-1">
                                    <KeyboardCombo keys={PromptInputModifierEnterKeyBinding} />
                                </div>
                            </div>
                            <div className="flex flex-row items-center justify-between gap-2">
                                <span>New line</span>
                                <div className="flex items-center gap-1">
                                    <KeyboardCombo keys={["Shift", "Enter"]} />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            }
            usePortal
        >
            {buttonElement}
        </Tooltip>
    );
}

function ChipArea({ onboarding }: { onboarding: boolean }) {
    const selectedContexts = useAtomValue(selectedContextsAtom);
    const selectedEnvironmentClass = useAtomValue(selectedEnvironmentClassAtom);
    const unresolvedProjectContexts = useAtomValue(unresolvedProjectContextsAtom);
    const unresolvedEnvironmentClass = useAtomValue(unresolvedEnvironmentClassAtom);
    const [selectedImages, setSelectedImages] = useAtom(selectedImagesAtom);
    const { value: imageAttachmentsEnabled } = useFlagAgentImageAttachmentsEnabled();

    const handleRemoveImage = useCallback(
        (id: string) => {
            setSelectedImages((prev) => prev.filter((img) => img.id !== id));
        },
        [setSelectedImages],
    );

    const hasContent =
        selectedContexts.length > 0 ||
        selectedEnvironmentClass ||
        unresolvedProjectContexts.length > 0 ||
        unresolvedEnvironmentClass ||
        (imageAttachmentsEnabled && selectedImages.length > 0);

    if (!hasContent) {
        return null;
    }

    function renderContextChip(context: SelectedContext) {
        let contextChip;
        let key;
        switch (context.kind) {
            case "project":
                contextChip = <ProjectContextChip context={context} />;
                key = `${context.kind}:${context.project.id}`;
                break;
            case "issue":
                contextChip = <IssueContextChip context={context} />;
                key = `${context.kind}:${context.ownerName}/${context.repoName}/${context.title}`;
                break;
            case "pr":
                contextChip = <PullRequestContextChip context={context} />;
                key = `${context.kind}:${context.ownerName}/${context.repoName}/${context.title}`;
                break;
            case "git-commit":
                contextChip = <GitCommitContextChip context={context} />;
                key = `${context.kind}:${context.ownerName}/${context.repoName}/${context.commitHash}`;
                break;
            case "git-tag":
                contextChip = <GitTagContextChip context={context} />;
                key = `${context.kind}:${context.ownerName}/${context.repoName}/${context.tagName}`;
                break;
            case "scratch":
                contextChip = <FromScrachContextChip context={context} />;
                key = context.kind;
                break;
            case "repo":
                contextChip = <RepoContextChip context={context} />;
                key = `${context.kind}:${context.ownerName}/${context.repoName}/${context.branchName}`;
                break;
            default:
                contextChip = null;
                break;
        }
        return (
            <li key={key} className="w-full max-w-full list-none sm:w-auto">
                {contextChip}
            </li>
        );
    }

    function renderUnresolvedProjectChip(context: UnresolvedProjectContext) {
        return (
            <li key={`unresolved:${context.projectId}`} className="w-full max-w-full list-none sm:w-auto">
                <UnresolvedProjectContextChip context={context} />
            </li>
        );
    }

    return (
        <div className="w-full max-w-full p-2">
            <ul className="flex w-full flex-row flex-wrap gap-1">
                {selectedContexts.map(renderContextChip)}
                {unresolvedProjectContexts.map(renderUnresolvedProjectChip)}
                {selectedEnvironmentClass ? (
                    <EnvironmentClassChip environmentClass={selectedEnvironmentClass} removable={!onboarding} />
                ) : unresolvedEnvironmentClass ? (
                    <UnresolvedEnvironmentClassChip environmentClass={unresolvedEnvironmentClass} />
                ) : null}
                {imageAttachmentsEnabled &&
                    selectedImages.map((image) => (
                        <li key={image.id} className="w-full max-w-full list-none sm:w-auto">
                            <ImageChip image={image} onRemove={handleRemoveImage} />
                        </li>
                    ))}
            </ul>
        </div>
    );
}

function SelectedCards({
    selectedCards,
    onCardDismiss,
}: {
    selectedCards: PredefinedTask[];
    onCardDismiss?: (taskName: PredefinedTask["taskName"]) => void;
}) {
    const height = useAtomValue(promptTextareaHeightAtom);

    return (
        <ul className="flex flex-wrap gap-2 pb-2 md:flex-row" style={{ minHeight: height }}>
            {selectedCards.map((card) => (
                <PromptTemplate
                    key={card.taskName}
                    title={card.title}
                    Icon={card.Icon}
                    iconColor={card.iconColor}
                    iconBackgroundColor={card.iconBackgroundColor}
                    description={card.description}
                    selected={true}
                    selectionBehavior="dismiss"
                    size="base"
                    data-tracking-id={`dismiss-prompt-task-card-${card.taskName}-prompt-input`}
                    onSelectChange={() => {
                        onCardDismiss?.(card.taskName);
                    }}
                    data-testid={`example-prompt-${card.taskName}`}
                />
            ))}
        </ul>
    );
}

function ActiveMenu() {
    const { preferredEnvironmentClassPerProject } = useGetPreferredEnvironmentClassPerProject();
    const [dropdownMenuKind, setDropdownMenuKind] = useAtom(dropdownMenuKindAtom);
    const [width] = useAtom(dropdownMenuWidthAtom);
    const store = useStore();
    const setSelectedContexts = useSetAtom(selectedContextsAtom);
    const setSelectedEnvironmentClass = useSetAtom(selectedEnvironmentClassAtom);
    const setContextAssociatedProjects = useSetAtom(contextAssociatedProjectsAtom);

    useEffect(() => {
        function listener(event: KeyboardEvent) {
            if (event.key === "Escape") {
                const menuKind = store.get(dropdownMenuKindAtom);
                if (
                    menuKind === DROPDOWN_MENU_KIND.context ||
                    menuKind === DROPDOWN_MENU_KIND.environmentClassSelector ||
                    menuKind === DROPDOWN_MENU_KIND.contextAssociatedProjectSelector ||
                    menuKind === DROPDOWN_MENU_KIND.repositoryBrowser
                ) {
                    setDropdownMenuKind(DROPDOWN_MENU_KIND.none);
                }
            }
        }
        document.addEventListener("keydown", listener);
        return () => {
            document.removeEventListener("keydown", listener);
        };
    }, [setDropdownMenuKind, store]);

    function onSelectContext(context: SelectedContext[], associatedProjects: PlainProject[]) {
        setDropdownMenuKind(DROPDOWN_MENU_KIND.none);
        setSelectedContexts((currentContexts) => [...currentContexts, ...context]);
        if (associatedProjects.length > 0) {
            setContextAssociatedProjects(associatedProjects);
        }

        const projectContext = context.find((ctx) => ctx.kind === "project");
        if (projectContext) {
            if (projectContext.environmentClasses.length === 1) {
                setSelectedEnvironmentClass(projectContext.environmentClasses[0]);
            } else {
                const preferredEnvironmentClassId = preferredEnvironmentClassPerProject.get(projectContext.project.id);
                const preferredEnvironmentClass = projectContext.environmentClasses.find(
                    ({ clazz }) => clazz.id === preferredEnvironmentClassId,
                );
                if (preferredEnvironmentClass) {
                    setSelectedEnvironmentClass(preferredEnvironmentClass);
                }
            }
            setDropdownMenuKind(DROPDOWN_MENU_KIND.none);
            return;
        }

        // For scratch context, open the environment class selector
        const scratchContext = context.find((ctx) => ctx.kind === "scratch");
        if (scratchContext) {
            setDropdownMenuKind(DROPDOWN_MENU_KIND.environmentClassSelector);
            return;
        }

        setDropdownMenuKind(DROPDOWN_MENU_KIND.none);
    }

    function selectEnvironmentClass(clazz: EnvironmentClassEntry) {
        setDropdownMenuKind(DROPDOWN_MENU_KIND.none);
        setSelectedEnvironmentClass(clazz);
    }

    switch (dropdownMenuKind) {
        case DROPDOWN_MENU_KIND.none:
            return null;
        case DROPDOWN_MENU_KIND.context:
            return <ContextSelectorMenu width={width} onSelectContext={onSelectContext} />;
        case DROPDOWN_MENU_KIND.contextAssociatedProjectSelector:
            return (
                <ContextSelectorMenu
                    width={width}
                    kind={ContextSelectorMenu.Kind.project}
                    onSelectContext={onSelectContext}
                />
            );
        case DROPDOWN_MENU_KIND.environmentClassSelector:
            return <EnvironmentClassSelector width={width} onSelect={selectEnvironmentClass} />;
        default:
            return null;
    }
}

/**
 * @deprecated this component was used in the old onboarding flow, but is no longer used and should be removed.
 */
function AddRepositoryButton() {
    const [menuKind, setMenuKind] = useAtom(dropdownMenuKindAtom);
    const selectedContexts = useAtomValue(selectedContextsAtom);

    if (selectedContexts.length > 0) {
        return null;
    }

    return (
        <PromptInputButton
            className="flex flex-1 items-center space-x-2"
            onClick={() => {
                setMenuKind((kind) =>
                    kind === DROPDOWN_MENU_KIND.none ? DROPDOWN_MENU_KIND.repositoryBrowser : DROPDOWN_MENU_KIND.none,
                );
            }}
            active={menuKind === DROPDOWN_MENU_KIND.repositoryBrowser}
            data-tracking-id="add-repository-prompt-input"
        >
            <GlobeIcon size={16} className="shrink-0" />
            <span className="flex-1 text-start">Add repository</span>
            <IconChevronDown size="sm" />
        </PromptInputButton>
    );
}

const ChipContextAdded: FC = () => {
    return (
        <div
            className="flex items-center gap-1.5 rounded-md bg-surface-success-subtle px-2.5 py-1.5"
            data-testid="chip-context-added"
        >
            <IconCircleCheckedFilled className="size-4 text-content-success" />
            <Text className="font-mono text-sm text-content-success">Context added</Text>
        </div>
    );
};

function isContextComplete(
    selectedContexts: SelectedContext[],
    selectedEnvironmentClass: EnvironmentClassEntry | null,
): boolean {
    if (selectedContexts.length === 0) {
        return false;
    }

    const context = selectedContexts[0]; // Assuming single context selection

    switch (context.kind) {
        case "project":
            return selectedEnvironmentClass !== null;
        case "pr":
        case "issue":
        case "repo":
        case "git-commit": {
            // URL contexts need either a project context or an environment class
            const hasProjectContext = selectedContexts.some((ctx) => ctx.kind === "project");
            return hasProjectContext && selectedEnvironmentClass !== null;
        }
        case "scratch":
            // Scratch context needs an environment class
            return selectedEnvironmentClass !== null;
        default:
            return false;
    }
}

function AddContextButton() {
    const [menuKind, setMenuKind] = useAtom(dropdownMenuKindAtom);
    const selectedContexts = useAtomValue(selectedContextsAtom);

    if (selectedContexts.length > 0) {
        return null;
    }

    return (
        <PromptInputButton
            className="flex flex-1 items-center space-x-2"
            onClick={() => {
                setMenuKind((kind) =>
                    kind === DROPDOWN_MENU_KIND.none ? DROPDOWN_MENU_KIND.context : DROPDOWN_MENU_KIND.none,
                );
            }}
            active={
                menuKind === DROPDOWN_MENU_KIND.context ||
                menuKind === DROPDOWN_MENU_KIND.projectSelector ||
                menuKind === DROPDOWN_MENU_KIND.urlSelector
            }
            data-tracking-id="add-context-prompt-input"
        >
            <FocusIcon size={16} className="shrink-0" />
            <span className="flex-1 text-start">Add context</span>
            <IconChevronDown size="sm" />
        </PromptInputButton>
    );
}

function SelectProjectButton() {
    const [menuKind, setMenuKind] = useAtom(dropdownMenuKindAtom);
    const contextAssociatedProjects = useAtomValue(contextAssociatedProjectsAtom);
    const selectedContexts = useAtomValue(selectedContextsAtom);
    const hasProjectContext = useAtomValue(projectContextAtom);
    const selectedEnvironmentClass = useAtomValue(selectedEnvironmentClassAtom);

    const canSelectProject =
        !selectedEnvironmentClass &&
        contextAssociatedProjects.length > 0 &&
        !hasProjectContext &&
        selectedContexts.length > 0;

    if (!canSelectProject) {
        return null;
    }

    return (
        <PromptInputButton
            className="flex items-center space-x-2"
            onClick={() => {
                setMenuKind((kind) =>
                    kind === DROPDOWN_MENU_KIND.none
                        ? DROPDOWN_MENU_KIND.contextAssociatedProjectSelector
                        : DROPDOWN_MENU_KIND.none,
                );
            }}
            active={menuKind === DROPDOWN_MENU_KIND.contextAssociatedProjectSelector}
            data-tracking-id="select-project-prompt-input"
        >
            <FolderIcon size={16} className="shrink-0" />
            <span className="flex-1 text-start">Select project</span>
            <IconChevronDown size="sm" />
        </PromptInputButton>
    );
}

function SelectEnvironmentClassButton() {
    const [menuKind, setMenuKind] = useAtom(dropdownMenuKindAtom);
    const selectedEnvironmentClass = useAtomValue(selectedEnvironmentClassAtom);
    const selectedContexts = useAtomValue(selectedContextsAtom);

    const canSelectClass = !selectedEnvironmentClass && selectedContexts.length > 0;

    if (!canSelectClass) {
        return null;
    }

    return (
        <PromptInputButton
            className="flex items-center space-x-2"
            onClick={() => {
                setMenuKind((kind) =>
                    kind === DROPDOWN_MENU_KIND.none
                        ? DROPDOWN_MENU_KIND.environmentClassSelector
                        : DROPDOWN_MENU_KIND.none,
                );
            }}
            active={menuKind === DROPDOWN_MENU_KIND.environmentClassSelector}
            data-tracking-id="select-environment-class-prompt-input"
        >
            <CpuIcon size={16} className="shrink-0" />
            <span className="flex-1 text-nowrap text-start">Environment class</span>
            <IconChevronDown size="sm" />
        </PromptInputButton>
    );
}

function PromptInputButton({ className, active, ...props }: React.ComponentProps<"button"> & { active: boolean }) {
    return (
        <button
            type="button"
            className={cn(
                "h-8 rounded-lg border border-border-base px-3 py-1 text-base opacity-80 outline-none ring-0 hover:bg-gray-200 focus:border-blue-500 active:border-blue-500 dark:hover:bg-neutral-700",
                { "border-blue-500 bg-gray-200 dark:bg-neutral-700": active },
                className,
            )}
            {...props}
        />
    );
}

function ConnectToGitHubButton({ isLoading, onClick }: { isLoading: boolean; onClick: () => void }) {
    return (
        <Tooltip content="Please connect your GitHub first" usePortal>
            <Button
                size="md"
                variant="primary"
                className="h-8 gap-2 rounded-lg bg-black text-white hover:bg-gray-900"
                type="button"
                loading={isLoading}
                LeadingIcon={IconGitHub}
                onClick={onClick}
                data-tracking-id="connect-to-github-prompt-input"
            >
                Connect to GitHub
            </Button>
        </Tooltip>
    );
}

function Chip({
    children,
    className,
    ...props
}: React.PropsWithChildren<{ className?: string }> & React.ComponentProps<"div">) {
    return (
        <div
            className={cn(
                "w-min max-w-full items-center rounded-md border border-border-subtle bg-surface-primary",
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );
}

function FromScrachContextChip({ context }: { context: FromScratchContext }) {
    const setSelectedContexts = useSetAtom(selectedContextsAtom);
    const setSelectedEnvironmentClass = useSetAtom(selectedEnvironmentClassAtom);

    function deleteContext() {
        setSelectedContexts((contexts) => contexts.filter((it) => it !== context));
        setSelectedEnvironmentClass(null);
    }

    return (
        <Chip className="flex items-center space-x-2 px-2 py-1">
            <IconFile size="sm" className="shrink-0" />
            <Text className="truncate text-sm font-medium">From scratch</Text>
            <CircleDeleteButton
                onClick={deleteContext}
                label="Delete from-scratch context"
                data-tracking-id="delete-scratch-context-prompt-input"
            />
        </Chip>
    );
}

function ProjectContextChip({ context }: { context: ProjectContext }) {
    const setSelectedContexts = useSetAtom(selectedContextsAtom);
    const setSelectedEnvironmentClass = useSetAtom(selectedEnvironmentClassAtom);
    const branch = useMemo(() => {
        const gitInitializer = getProjectInitializer(context.project.initializer);
        return gitInitializer?.cloneTarget;
    }, [context]);

    return (
        <Chip className="flex items-center space-x-2 px-2 py-1" data-testid="project-context-chip">
            <FolderIcon size={16} className="shrink-0" />
            <Text className="truncate text-sm font-medium">{context.project.metadata?.name || context.project.id}</Text>
            <Text className="truncate font-mono text-sm text-content-secondary/80">{branch}</Text>
            <CircleDeleteButton
                onClick={() => {
                    setSelectedContexts((contexts) => contexts.filter((it) => it !== context));
                    setSelectedEnvironmentClass(null);
                }}
                label={`Delete "${context.project.metadata?.name || context.project.id}" project context`}
                data-tracking-id="delete-project-context-prompt-input"
            />
        </Chip>
    );
}

function UnresolvedProjectContextChip({ context }: { context: UnresolvedProjectContext }) {
    return (
        <Chip className="flex items-center space-x-2 px-2 py-1" data-testid="unresolved-project-context-chip">
            <FolderIcon size={16} className="shrink-0" />
            <Text className="truncate text-sm font-medium">{context.projectName || context.projectId}</Text>
            <IconSpinner size="sm" className="shrink-0 animate-spin text-content-secondary" />
        </Chip>
    );
}

function UnresolvedEnvironmentClassChip({ environmentClass }: { environmentClass: StoredEnvironmentClass }) {
    return (
        <Chip className="flex items-center space-x-2 px-2 py-1">
            <CpuIcon size={16} className="shrink-0" />
            <Text className="text-nowrap text-sm font-medium">{environmentClass.displayName}</Text>
            <IconSpinner size="sm" className="shrink-0 animate-spin text-content-secondary" />
        </Chip>
    );
}

function GitCommitContextChip({ context }: { context: GitCommitContext }) {
    const setSelectedContexts = useSetAtom(selectedContextsAtom);
    const setContextAssociatedProjects = useSetAtom(contextAssociatedProjectsAtom);

    function deleteContext() {
        setSelectedContexts((contexts) => contexts.filter((it) => it !== context));
        setContextAssociatedProjects([]);
    }

    return (
        <Chip className="flex items-center space-x-2 px-2 py-1">
            <SourceControlProviderIcon scmId={context.scmId} size="sm" />
            <Text className="text-nowrap text-sm font-medium">
                {context.ownerName}/{context.repoName}
            </Text>
            <GitCommitVerticalIcon size={16} className="shrink-0" />
            <Text className="truncate font-mono text-sm text-content-secondary/80">
                {context.commitHash.substring(0, 7)}
            </Text>
            <CircleDeleteButton
                onClick={deleteContext}
                label={`Delete "${context.ownerName}/${context.repoName}" commit context`}
                data-tracking-id="delete-commit-context-prompt-input"
            />
        </Chip>
    );
}

function RepoContextChip({ context }: { context: RepoContext }) {
    const setSelectedContexts = useSetAtom(selectedContextsAtom);
    const setContextAssociatedProjects = useSetAtom(contextAssociatedProjectsAtom);

    function deleteContext() {
        setSelectedContexts((contexts) => contexts.filter((it) => it !== context));
        setContextAssociatedProjects([]);
    }

    return (
        <Chip className="flex items-center space-x-2 px-2 py-1">
            <SourceControlProviderIcon scmId={context.scmId} size="sm" />
            <Text className="text-nowrap text-sm font-medium">
                {context.ownerName}/{context.repoName}
            </Text>
            <Text className="truncate font-mono text-sm text-content-secondary/80">{context.branchName}</Text>
            <CircleDeleteButton
                onClick={deleteContext}
                label={`Delete "${context.ownerName}/${context.repoName}" repository context`}
                data-tracking-id="delete-repository-context-prompt-input"
            />
        </Chip>
    );
}

function GitTagContextChip({ context }: { context: GitTagContext }) {
    const setSelectedContexts = useSetAtom(selectedContextsAtom);
    const setContextAssociatedProjects = useSetAtom(contextAssociatedProjectsAtom);

    function deleteContext() {
        setSelectedContexts((contexts) => contexts.filter((it) => it !== context));
        setContextAssociatedProjects([]);
    }

    return (
        <Chip className="flex items-center space-x-2 px-2 py-1">
            <SourceControlProviderIcon scmId={context.scmId} size="sm" />
            <Text className="text-nowrap text-sm font-medium">
                {context.ownerName}/{context.repoName}
            </Text>
            <TagIcon size={16} className="shrink-0" />
            <Text className="truncate font-mono text-sm text-content-secondary/80">tag/{context.tagName}</Text>
            <CircleDeleteButton
                onClick={deleteContext}
                label={`Delete "${context.ownerName}/${context.repoName}" tag context`}
                data-tracking-id="delete-tag-context-prompt-input"
            />
        </Chip>
    );
}

function IssueContextChip({ context }: { context: IssueContext }) {
    const setSelectedContexts = useSetAtom(selectedContextsAtom);
    const setContextAssociatedProjects = useSetAtom(contextAssociatedProjectsAtom);

    function deleteContext() {
        setSelectedContexts((contexts) => contexts.filter((it) => it !== context));
        setContextAssociatedProjects([]);
    }

    return (
        <Chip className="flex items-center space-x-2 px-2 py-1">
            <SourceControlProviderIcon scmId={context.scmId} size="sm" />
            <Text className="text-nowrap text-sm font-medium">
                {context.ownerName}/{context.repoName}
            </Text>
            <CircleDot size={16} className="shrink-0" />
            <Text className="truncate font-mono text-sm text-content-secondary/80">{context.title}</Text>
            <CircleDeleteButton
                onClick={deleteContext}
                label={`Delete "${context.title}" issue context`}
                data-tracking-id="delete-issue-context-prompt-input"
            />
        </Chip>
    );
}

function PullRequestContextChip({ context }: { context: PullRequestContext }) {
    const setSelectedContexts = useSetAtom(selectedContextsAtom);
    const setContextAssociatedProjects = useSetAtom(contextAssociatedProjectsAtom);

    function deleteContext() {
        setSelectedContexts((contexts) => contexts.filter((it) => it !== context));
        setContextAssociatedProjects([]);
    }

    return (
        <Chip className="flex items-center space-x-2 px-2 py-1">
            <SourceControlProviderIcon scmId={context.scmId} size="sm" />
            <Text className="text-nowrap text-sm font-medium">
                {context.ownerName}/{context.repoName}
            </Text>
            <GitPullRequest size={16} className="shrink-0" />
            <Text className="truncate font-mono text-sm text-content-secondary/80">{context.title}</Text>
            <CircleDeleteButton
                onClick={deleteContext}
                label={`Delete "${context.title}" pull request context`}
                data-tracking-id="delete-pr-context-prompt-input"
            />
        </Chip>
    );
}

function EnvironmentClassChip({
    environmentClass,
    removable,
}: {
    environmentClass: EnvironmentClassEntry;
    removable: boolean;
}) {
    const setSelectedEnvironmentClass = useSetAtom(selectedEnvironmentClassAtom);
    return (
        <Chip className="flex items-center space-x-2 px-2 py-1">
            <CpuIcon size={16} className="shrink-0" />
            <Text className="text-nowrap text-sm font-medium">{environmentClass.clazz.displayName}</Text>
            <Text className="truncate font-mono text-sm text-content-secondary/80">
                {environmentClass.clazz.description}
            </Text>
            {removable ? (
                <CircleDeleteButton
                    onClick={() => {
                        setSelectedEnvironmentClass(null);
                    }}
                    label={`Delete "${environmentClass.clazz.displayName}" environment class`}
                    data-tracking-id="delete-environment-class-prompt-input"
                />
            ) : null}
        </Chip>
    );
}

function CircleDeleteButton({
    className,
    onClick,
    label = "Delete item",
    "data-tracking-id": dataTrackingId,
}: {
    className?: string;
    onClick: () => void;
    label?: string;
    "data-tracking-id": string;
}) {
    return (
        <button
            className={cn("rounded-full text-content-muted hover:text-content-accent", className)}
            onClick={onClick}
            data-tracking-id={dataTrackingId}
        >
            <span className="sr-only">{label}</span>
            <IconCircleX className="size-4.5 shrink-0" />
        </button>
    );
}
